import {
  Button,
  Callout,
  Card,
  CardLevel,
  Classes,
  Col,
  FormGroup,
  Frow,
  Intent,
  ISelectOption,
  MultiSelect,
  PaddingLevel,
  Subheading2,
} from '@pinpointhq/thumbtack';
import * as React from 'react';

export type ICondition = { [key: string]: string[] };

interface IConditionsEditor {
  conditions: ICondition[];
  onChange: (conditions: ICondition[]) => void;
  educationLevels: ISelectOption[];
  yearsOfGraduation: ISelectOption[];
  degreeSubjects: ISelectOption[];
  skillOptions: ISelectOption[];
}

// Candidate filters for an allocation. Each condition is ANDed internally and conditions are ORed together, matching
// AllocationRun.additional_query_string.
export function ConditionsEditor({
  conditions,
  onChange,
  educationLevels,
  yearsOfGraduation,
  degreeSubjects,
  skillOptions,
}: IConditionsEditor) {
  const fields = [
    { key: 'level_of_education', label: 'Level of education', options: educationLevels },
    { key: 'year_of_graduation', label: 'Year of graduation', options: yearsOfGraduation },
    { key: 'degree_subject', label: 'Degree', options: degreeSubjects },
    { key: 'skills', label: 'Skills', options: skillOptions },
  ];

  const updateCondition = (index: number, key: string, values: string[]) => {
    onChange(conditions.map((condition, i) => (i === index ? { ...condition, [key]: values } : condition)));
  };

  const removeCondition = (index: number) => onChange(conditions.filter((_, i) => i !== index));

  return (
    <>
      <Callout intent={Intent.PRIMARY} className="mar-b-3">
        With no conditions every eligible candidate is included. A candidate needs to match everything in at least one
        condition, so "graduating 2027 and final year" OR "graduating 2028 and penultimate year" is two conditions.
      </Callout>
      <Frow verticalGutters={16} flexDirection="column">
        {conditions.map((condition, index) => (
          <Col key={index}>
            <Card paddingLevel={PaddingLevel.REGULAR} level={CardLevel.OUTLINED}>
              <Frow justifyContent="space-between" alignItems="center">
                <Subheading2 className="mar-b-2">Condition {index + 1}</Subheading2>
                <Button minimal={true} text="Remove" onClick={() => removeCondition(index)} />
              </Frow>
              {fields.map((field) => (
                <FormGroup key={field.key} label={field.label}>
                  <MultiSelect
                    items={field.options}
                    selectedItems={field.options.filter((option) => (condition[field.key] || []).includes(option.value))}
                    handleChange={(selected: ISelectOption[]) =>
                      updateCondition(
                        index,
                        field.key,
                        selected.map((item) => item.value),
                      )
                    }
                    fill={true}
                    tagInputProps={{ tagProps: { minimal: true, className: Classes.MUTED } }}
                  />
                </FormGroup>
              ))}
            </Card>
          </Col>
        ))}
        <Col>
          <Button minimal={true} fill={true} onClick={() => onChange([...conditions, {}])}>
            Add condition
          </Button>
        </Col>
      </Frow>
    </>
  );
}
